import { Badge } from "@/components/ui/badge";
import { Column } from "@/components/ui/flex";
import { Label } from "@/components/ui/label";
import { useGroupFoldersData } from "@/data/use-group-folder-data";
import { useGroupSkillsData } from "@/data/use-group-skills-data";
import { useGroupTemplatesData } from "@/data/use-templates-data";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import { cn } from "@/lib/utils";
import { Link, type LinkProps } from "@tanstack/react-router";

const KIND_LABEL: Record<string, string> = {
  knowledge_file: "Knowledge",
  project_template: "Template",
  skill: "Skill",
};

const KIND_ORDER = ["knowledge_file", "project_template", "skill"] as const;

type ContentDependencyItem = {
  kind: string;
  uuid: string;
  name: string | null;
  available?: boolean;
};

type ContentDependencyGroup = {
  kind: string;
  label: string;
  items: ContentDependencyItem[];
};

function isContentDependencyUnavailable(
  dependency: ContentDependencyItem,
): boolean {
  if (typeof dependency.available === "boolean") {
    return !dependency.available;
  }
  return dependency.name === null;
}

export function formatContentDependencyLabel(
  dependency: ContentDependencyItem,
): string {
  if (isContentDependencyUnavailable(dependency)) {
    return dependency.name ? `${dependency.name} (Missing)` : "Missing";
  }
  return dependency.name ?? "Removed";
}

export function getContentDependencyErrorMessage(
  error: unknown,
): string | null {
  if (!error || typeof error !== "object") {
    return null;
  }

  const body = error as {
    payload?: { response?: { message?: string; issues?: unknown[] } };
    response?: { message?: string; issues?: unknown[] };
    userResponse?: { message?: string };
  };
  const response = body.payload?.response ?? body.response;
  if (
    response?.issues &&
    Array.isArray(response.issues) &&
    response.issues.length > 0 &&
    response.message
  ) {
    return response.message;
  }

  return body.userResponse?.message ?? response?.message ?? null;
}

export function groupContentDependencies(
  dependencies: ContentDependencyItem[],
): ContentDependencyGroup[] {
  const groups: ContentDependencyGroup[] = KIND_ORDER.map((kind) => ({
    kind,
    label: KIND_LABEL[kind],
    items: dependencies.filter((dependency) => dependency.kind === kind),
  })).filter((group) => group.items.length > 0);

  const knownKinds = new Set<string>(KIND_ORDER);
  const otherItems = dependencies.filter(
    (dependency) => !knownKinds.has(dependency.kind),
  );
  if (otherItems.length > 0) {
    groups.push({
      kind: "other",
      label: "Other",
      items: otherItems,
    });
  }

  return groups;
}

export function getContentDependencyHref(params: {
  dependency: ContentDependencyItem;
  groupSlug: string | null;
  kitUuid: string | null;
  templates: Array<{ uuid: string; kitUuid?: string | null }>;
  skills: Array<{ uuid: string; creatorKitUuid?: string | null }>;
  files: Array<{ uuid: string; folderUuid: string }>;
}): LinkProps | null {
  const { dependency, groupSlug, kitUuid, templates, skills, files } = params;

  if (isContentDependencyUnavailable(dependency) || !groupSlug) {
    return null;
  }

  if (dependency.kind === "skill") {
    const skill = skills.find((item) => item.uuid === dependency.uuid);
    if (!skill?.creatorKitUuid) {
      return null;
    }
    return {
      to: "/w/$groupSlug/k/$kitUuid/skill",
      params: {
        groupSlug,
        kitUuid: skill.creatorKitUuid,
      },
    };
  }

  if (dependency.kind === "project_template") {
    const template = templates.find((item) => item.uuid === dependency.uuid);
    if (!template?.kitUuid) {
      return null;
    }
    return {
      to: "/w/$groupSlug/k/$kitUuid/template",
      params: {
        groupSlug,
        kitUuid: template.kitUuid,
      },
    };
  }

  if (dependency.kind === "knowledge_file" && kitUuid) {
    const file = files.find((item) => item.uuid === dependency.uuid);
    if (!file) {
      return null;
    }
    return {
      to: "/w/$groupSlug/k/$kitUuid/knowledge/$folderUuid/$",
      params: {
        groupSlug,
        kitUuid,
        folderUuid: file.folderUuid,
        _splat: "",
      },
    };
  }

  return null;
}

export function DependenciesList({
  dependencies,
}: {
  dependencies: ContentDependencyItem[];
}) {
  const getHref = useDependencyHref();

  return (
    <Column className="gap-2">
      <Label>Dependencies</Label>
      {dependencies.length === 0 ? (
        <p className="text-muted-foreground text-sm">None</p>
      ) : (
        <ul className="list-disc space-y-1 pl-5">
          {groupContentDependencies(dependencies)
            .flatMap((group) => group.items)
            .map((dependency) => {
              const href = getHref(dependency);
              const unavailable = isContentDependencyUnavailable(dependency);
              const label = formatContentDependencyLabel(dependency);

              return (
                <li key={`${dependency.kind}:${dependency.uuid}`}>
                  <span className="inline-flex items-center gap-2 align-middle">
                    <Badge variant="outline" className="text-xs">
                      {KIND_LABEL[dependency.kind] ?? dependency.kind}
                    </Badge>
                    {href ? (
                      <Link
                        {...href}
                        className="text-sm hover:underline"
                        title={label}
                      >
                        {label}
                      </Link>
                    ) : (
                      <span
                        className={cn(
                          "text-sm",
                          unavailable ? "text-muted-foreground" : undefined,
                        )}
                      >
                        {label}
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
        </ul>
      )}
    </Column>
  );
}

function useDependencyHref() {
  const groupSlug = useGroupSlug(false);
  const kitUuid = useKitUuid();
  const groupUuid = useGroupUuid();
  const { files } = useGroupFoldersData({ groupUuid });
  const { templates } = useGroupTemplatesData({ groupUuid });
  const { skills } = useGroupSkillsData({ groupUuid });

  return (dependency: ContentDependencyItem): LinkProps | null =>
    getContentDependencyHref({
      dependency,
      groupSlug: groupSlug ?? null,
      kitUuid,
      templates,
      skills,
      files,
    });
}
